/**
 * 저작 규칙의 단일 출처 — CLI(`npm run pack:author`)와 관리자 콘솔(`admin/`)이 **같은 함수**를 쓴다.
 * 여기 있는 것은 전부 순수 함수다(파일·네트워크·시계 없음). 검증은 엔진의 `packIssues` 하나뿐이다.
 *
 * 두 축을 구분한다 (ADR-041):
 *  - **유형(topic)** — 아재개그·나라·도시·음식…. 문제가 쌓이는 풀. 개수 제한이 없다.
 *  - **팩(pack)**   — 유형 하나 이상에서 골라 **24제**로 조립한 배포 단위. 규격은 로케일 프로필 × 3(RULES §1.17).
 */
import {
  type Alphabet,
  LOCALES,
  type Locale,
  type LocaleProfile,
  packShapeFor,
} from '../engine/alphabet';
import {
  BASIC_JUNG,
  CHO_COMMON,
  JONG_COMMON,
  JUNG_COMMON,
  LADDER_BLOCKLIST,
  compose,
  decompose,
  isPalatalizationViolation,
  isSyllable,
  normalizeJamo,
} from '../engine/hangul';
import {
  type ContentPack,
  PACK_SIZE,
  type PackIssue,
  type PackKind,
  type Question,
  packIssues,
} from '../engine/pack';

const BLOCK = new Set(LADDER_BLOCKLIST);
const DECOYS_WANT = 6;

// ── 오답 자동 채움(한글) ──────────────────────────────────────────────────

/** v 에서 가까운 순(동률은 작은 번호) — 자모 번호가 사전 순이라 이웃일수록 닮았다(ㅋ↔ㅌ↔ㅍ↔ㅎ) */
function byDist(list: readonly number[], v: number): number[] {
  return list.filter((x) => x !== v).sort((a, b) => Math.abs(a - v) - Math.abs(b - v) || a - b);
}

/**
 * 정답 음절의 자모 하나를 흔한 자모로 바꾼 이웃 — 정답 음절·사다리 블록리스트·구개음화 위반은 제외.
 * 초성 변형 → 종성 변형 → 중성 변형을 한 장씩 번갈아 내놓아 6장이 한 부류로 쏠리지 않게 한다.
 * 중성은 기본 모음(ㅏㅓㅗㅜㅡㅣ)을 먼저, ㅑㅕㅛㅠ 계열은 뒤로 — 사문화 음절이 앞에 오지 않게.
 */
export function neighbors(ch: string, forbid: ReadonlySet<string>): string[] {
  const j = decompose(ch);
  const seen = new Set<string>();
  const ok = (cho: number, jung: number, jong: number): string | null => {
    if (isPalatalizationViolation(cho, jung)) return null;
    const c = compose(cho, jung, jong);
    if (!isSyllable(c) || forbid.has(c) || BLOCK.has(c) || seen.has(c)) return null;
    seen.add(c);
    return c;
  };
  const choL = byDist(CHO_COMMON, j.cho).map((cho) => ok(cho, j.jung, j.jong));
  const jongL = byDist(JONG_COMMON, j.jong).map((jong) => ok(j.cho, j.jung, jong));
  const basic = JUNG_COMMON.filter((x) => BASIC_JUNG.has(x));
  const rest = JUNG_COMMON.filter((x) => !BASIC_JUNG.has(x));
  const jungL = [...byDist(basic, j.jung), ...byDist(rest, j.jung)].map((jung) =>
    ok(j.cho, jung, j.jong),
  );
  const out: string[] = [];
  const n = Math.max(choL.length, jongL.length, jungL.length);
  for (let i = 0; i < n; i++) {
    for (const list of [choL, jongL, jungL]) {
      const c = list[i];
      if (c !== undefined && c !== null) out.push(c);
    }
  }
  return out;
}

function hangulDecoys(a: string, want: number): string[] {
  const chars = a.split('');
  const forbid = new Set(chars);
  const picked: string[] = [];
  const seen = new Set<string>();
  const take = (c: string): void => {
    if (seen.has(c)) return;
    seen.add(c);
    picked.push(c);
  };
  const per = chars.map((ch) => neighbors(ch, forbid));
  // ① 규칙 13 — 희귀 자모(정규화가 바꾸는 자모)마다 그 부류를 보존한 이웃을 2장 먼저 넣는다
  chars.forEach((ch, i) => {
    const raw = decompose(ch);
    const norm = normalizeJamo(raw);
    for (const slot of ['cho', 'jung', 'jong'] as const) {
      if (raw[slot] === norm[slot]) continue;
      const covered = picked.filter((c) => normalizeJamo(decompose(c))[slot] === norm[slot]).length;
      const keep = (per[i] ?? []).filter((c) => normalizeJamo(decompose(c))[slot] === norm[slot]);
      for (const c of keep.slice(0, Math.max(0, 2 - covered))) take(c);
    }
  });
  // ② 그 뒤 정답 음절 라운드로빈으로 want 장까지 채운다
  for (let round = 0; picked.length < want && round < 24; round++) {
    for (const list of per) {
      const c = list[round];
      if (c !== undefined) take(c);
      if (picked.length >= want) break;
    }
  }
  return picked;
}

/** authored 오답 채우기 — 알파벳이 제안 함수를 가지면(라틴) 그것을, 아니면 한글 자모 이웃을 쓴다. */
export function fillDecoys(
  a: string,
  want = DECOYS_WANT,
  alphabet: Alphabet = LOCALES.ko.alphabet,
): string[] {
  if (alphabet.suggestDecoys !== null) return alphabet.suggestDecoys(a, want);
  return hangulDecoys(a, want);
}

// ── 풀(유형별 문제) ──────────────────────────────────────────────────────

/** 콘솔의 문제 풀 한 줄. `id` 는 풀 안에서 유일하고 발행 후에도 바뀌지 않는다(추적용). */
export interface PoolQuestion {
  readonly id: string; // 예 'country-014' — 팩 안의 c001 과 다르다
  readonly topicId: string;
  readonly q: string;
  readonly a: string;
  readonly d?: readonly string[]; // 비었으면 fillDecoys 가 채운다
  readonly hints?: readonly string[];
  readonly tags?: readonly string[];
}

/** 한글 정답 정규식 — 로케일이 정해지지 않은 옛 호출부용. 새 코드는 `LOCALES[locale].answerRe` 를 쓴다 */
export const ANSWER_RE = /^[가-힣]{2,4}$/;

/** 팩에 넣기 전 한 문제만 보는 검사 — 편집 중 즉시 뜨는 빨간 줄이다. 팩 단위 규칙(1·2)은 여기서 못 본다. */
export function questionIssues(q: PoolQuestion, prof: LocaleProfile = LOCALES.ko): string[] {
  const out: string[] = [];
  if (!prof.answerRe.test(q.a)) {
    out.push(`정답은 ${prof.alphabet.charLabel} ${prof.minLen}~${prof.maxLen}자여야 한다`);
  } else if (new Set(q.a.split('')).size !== q.a.length)
    out.push('정답에 같은 글자가 두 번 나온다');
  if (q.q.length < 6 || q.q.length > 40) out.push(`문제문은 6~40자다 (지금 ${q.q.length}자)`);
  if (q.a !== '' && q.q.includes(q.a)) out.push('문제문에 정답이 그대로 들어 있다');
  for (const h of q.hints ?? []) {
    if (h.length < 1 || h.length > 12) out.push(`힌트 '${h}' 는 1~12자여야 한다`);
    if (q.a !== '' && h.includes(q.a)) out.push(`힌트 '${h}' 에 정답이 들어 있다`);
  }
  return out;
}

/** 길이별 개수. 팩 조립 화면의 게이지가 이걸 그린다. */
export function poolShape(
  qs: readonly { readonly a: string }[],
  prof: LocaleProfile = LOCALES.ko,
): Record<number, number> {
  const out: Record<number, number> = {};
  for (const L of Object.keys(packShapeFor(prof.wordLenProfile))) out[Number(L)] = 0;
  for (const q of qs) {
    const L = q.a.length;
    if (L >= prof.minLen && L <= prof.maxLen) out[L] = (out[L] ?? 0) + 1;
  }
  return out;
}

/** 규격까지 몇 개가 모자라는가(음수면 초과). 조립 전에 사람에게 보여 준다. */
export function shapeGap(
  qs: readonly { readonly a: string }[],
  prof: LocaleProfile = LOCALES.ko,
): Record<number, number> {
  const have = poolShape(qs, prof);
  const want = packShapeFor(prof.wordLenProfile);
  const out: Record<number, number> = {};
  for (const L of Object.keys(want).map(Number)) out[L] = (want[L] as number) - (have[L] ?? 0);
  return out;
}

// ── 팩 조립 · 발행 ───────────────────────────────────────────────────────

export interface AssembleResult {
  readonly pack: ContentPack;
  /** pack.questions[i] 가 온 풀 문제의 id — 발행본에 함께 저장해 역추적한다 */
  readonly sourceIds: readonly string[];
  readonly issues: readonly PackIssue[];
}

/** c001 … c024 */
const slotId = (i: number): string => `c${String(i + 1).padStart(3, '0')}`;

/**
 * 풀에서 고른 문제들을 팩 하나로 조립한다. **정확히 24제**여야 하고 로케일 규격을 지켜야 한다.
 * 팩 안 번호(c001~c024)는 조립이 붙이고, 풀 id 는 `sourceIds` 로 남는다 — 순서는 길이 오름차순(사람이 읽기 좋게).
 * 팩 파일의 순서는 게임에 영향이 없다(`orderQuestions` 가 id 해시로 다시 정렬한다).
 */
export function assemblePack(
  selected: readonly PoolQuestion[],
  meta: {
    readonly id: string;
    readonly version: string;
    readonly kind: PackKind;
    readonly locale?: Locale;
  },
): AssembleResult {
  const locale: Locale = meta.locale ?? 'ko';
  const alphabet = LOCALES[locale].alphabet;
  const ordered = [...selected].sort((x, y) => x.a.length - y.a.length || (x.id < y.id ? -1 : 1));
  const questions: Question[] = ordered.map((q, i) => {
    const base: Question = {
      id: slotId(i),
      q: q.q,
      a: q.a,
      d: q.d !== undefined && q.d.length > 0 ? [...q.d] : fillDecoys(q.a, DECOYS_WANT, alphabet),
    };
    const withTags = q.tags !== undefined && q.tags.length > 0 ? { ...base, tags: q.tags } : base;
    return q.hints !== undefined && q.hints.length > 0 ? { ...withTags, hints: q.hints } : withTags;
  });
  const pack: ContentPack =
    locale === 'ko'
      ? { id: meta.id, version: meta.version, kind: meta.kind, questions }
      : { id: meta.id, version: meta.version, kind: meta.kind, locale, questions };
  const issues: PackIssue[] = [];
  if (selected.length !== PACK_SIZE) {
    issues.push({
      rule: 1,
      id: meta.id,
      message: `고른 문제 ${selected.length}제 !== ${PACK_SIZE}`,
    });
  }
  const dupPool = new Set<string>();
  for (const q of selected) {
    if (dupPool.has(q.id)) issues.push({ rule: 3, id: q.id, message: '같은 문제를 두 번 골랐다' });
    dupPool.add(q.id);
  }
  issues.push(...packIssues(pack));
  return { pack, sourceIds: ordered.map((q) => q.id), issues };
}

/** semver patch/minor/major 만 받는다. 발행본은 불변이라 같은 버전을 두 번 쓸 수 없다. */
export const VERSION_RE = /^\d+\.\d+\.\d+$/;

export function nextVersion(prev: string | null, bump: 'patch' | 'minor' | 'major'): string {
  const [a, b, c] = (prev ?? '0.0.0').split('.').map((n) => Number(n) || 0) as [
    number,
    number,
    number,
  ];
  if (bump === 'major') return `${a + 1}.0.0`;
  if (bump === 'minor') return `${a}.${b + 1}.0`;
  return `${a}.${b}.${c + 1}`;
}

/** 번들용 TS 파일 본문 — 번들은 서비스 장애의 폴백이자 첫 배포의 시드다(ADR-046). */
export function exportPackTs(pack: ContentPack): string {
  const name = `PACK_${pack.id.toUpperCase().replace(/[^A-Z0-9]/g, '_')}_${pack.version.replace(/\./g, '_')}`;
  const body = JSON.stringify(pack, null, 2).replace(/"([a-zA-Z_]+)":/g, '$1:');
  return `import type { ContentPack } from '../engine/pack';\n\n/** ${pack.id}@${pack.version} — 관리자 콘솔이 발행했다. 손으로 고치면 packHash 가 바뀐다(patch bump). */\nexport const ${name}: ContentPack = ${body};\n`;
}

// ── CLI 초안(JSON) 경로 ──────────────────────────────────────────────────

export interface Draft {
  readonly id: string;
  readonly version: string;
  readonly kind?: PackKind;
  readonly locale?: Locale;
  readonly questions: readonly (Omit<Question, 'd'> & { readonly d?: readonly string[] })[];
}

/** 초안 JSON → 팩(오답 채움). 검증은 호출자가 `packIssues`·`validatePack` 으로 한다. */
export function buildPack(draft: Draft): ContentPack {
  const alphabet = LOCALES[draft.locale ?? 'ko'].alphabet;
  const questions: Question[] = draft.questions.map((q) => {
    const base: Question = {
      id: q.id,
      q: q.q,
      a: q.a,
      d: q.d ?? fillDecoys(q.a, DECOYS_WANT, alphabet),
    };
    const withTags = q.tags !== undefined ? { ...base, tags: q.tags } : base;
    return q.hints !== undefined ? { ...withTags, hints: q.hints } : withTags;
  });
  const base: ContentPack =
    draft.kind !== undefined
      ? { id: draft.id, version: draft.version, kind: draft.kind, questions }
      : { id: draft.id, version: draft.version, questions };
  return draft.locale !== undefined && draft.locale !== 'ko'
    ? { ...base, locale: draft.locale }
    : base;
}
